import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErr, mediaUrl } from "@/lib/api";
import { Table, Input, Select, Button, Badge, Field, SortControl, sortRows } from "@/lib/ui";

const BLANK = { phone: "", seller_type: "retailer", company_name: "", trade_name: "", gst: "", pan: "",
  address: "", city: "", locality: "", contact_person: "", whatsapp: "", email: "", stock_location: "",
  years_in_business: 0, subscription_plan: "", subscription_status: "" };

export default function Sellers() {
  const [rows, setRows] = useState([]);
  const [payments, setPayments] = useState([]);
  const [q, setQ] = useState({ seller_type: "", search: "" });
  const [modal, setModal] = useState(null); // {mode, data}
  const [shot, setShot] = useState(null);
  const [sort, setSort] = useState({ field: "company_name", dir: "asc" });
  const SORT_FIELDS = [
    { key: "company_name", label: "Company", type: "str" },
    { key: "seller_type", label: "Type", type: "str" },
    { key: "city", label: "City", type: "str" },
    { key: "phone", label: "Mobile", type: "str" },
    { key: "product_count", label: "Products Listed", type: "num" },
  ];

  const load = useCallback(() => {
    const params = {};
    Object.entries(q).forEach(([k, v]) => v && (params[k] = v));
    api.get("/admin/sellers", { params }).then(({ data }) => setRows(data));
    api.get("/admin/payments").then(({ data }) => setPayments(data)).catch(() => setPayments([]));
  }, [q]);

  const latestPayment = (sellerId) => payments.find((p) => p.seller_id === sellerId);
  const payAction = async (id, status) => { try { await api.post(`/admin/payments/${id}/status?status=${status}`); toast.success(`Payment ${status}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const toggleVis = async (id) => { try { const { data } = await api.post(`/admin/sellers/${id}/visibility`); toast.success(`Visibility ${data.visibility ? "ON" : "OFF"}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const setBadge = async (id, status) => { try { await api.post(`/admin/sellers/${id}/badge`, { status }); toast.success(`Badge ${status}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const resetPw = async (s) => { if (!window.confirm(`Reset password for ${s.company_name || s.phone} to their mobile number? They will be forced to set a new password on next login.`)) return; try { await api.post(`/admin/sellers/${s.id}/reset-password`); toast.success("Password reset to mobile number — seller must change it on next login"); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (s) => { if (!window.confirm(`Delete ${s.company_name || s.phone}? This removes their products too.`)) return; try { await api.delete(`/admin/sellers/${s.id}`); toast.success("Seller deleted"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const editSlots = async (s) => {
    const cur = s.included_slots ?? 0;
    const val = window.prompt(`Set the number of products allowed for ${s.company_name || s.phone}`, cur);
    if (val === null) return;
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0) { toast.error("Enter a valid number"); return; }
    try { await api.put(`/admin/sellers/${s.id}`, { included_slots: n }); toast.success("Number of products updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openCreate = () => setModal({ mode: "create", data: { ...BLANK } });
  const openEdit = (s) => setModal({ mode: "edit", id: s.id, data: {
    ...BLANK, ...s, subscription_plan: s.subscription?.plan || "", subscription_status: s.subscription?.status || "" } });

  const save = async () => {
    const d = modal.data;
    try {
      if (modal.mode === "create") {
        await api.post("/admin/sellers", { phone: d.phone, seller_type: d.seller_type, company_name: d.company_name,
          city: d.city, locality: d.locality, contact_person: d.contact_person, whatsapp: d.whatsapp, email: d.email });
        toast.success("Seller created (temp password = mobile number)");
      } else {
        await api.put(`/admin/sellers/${modal.id}`, { ...d, years_in_business: Number(d.years_in_business) || 0 });
        toast.success("Seller updated");
      }
      setModal(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setF = (k, v) => setModal((m) => ({ ...m, data: { ...m.data, [k]: v } }));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-head text-2xl font-extrabold text-slate-900">All Sellers</h1>
        <Button onClick={openCreate} data-testid="seller-create-btn"><i className="fa-solid fa-plus" /> Create Seller</Button>
      </div>
      <div className="mt-4 mb-4 flex flex-wrap gap-2">
        <Input className="max-w-xs" placeholder="Search company / mobile / GST…" value={q.search} onChange={(e) => setQ((p) => ({ ...p, search: e.target.value }))} data-testid="sellers-search" />
        <Select className="w-auto" value={q.seller_type} onChange={(e) => setQ((p) => ({ ...p, seller_type: e.target.value }))} data-testid="sellers-type">
          <option value="">All Types</option><option value="retailer">Retailers</option><option value="wholesaler">Wholesalers</option><option value="manufacturer">Manufacturers</option>
        </Select>
        <Button variant="outline" onClick={() => setQ({ seller_type: "", search: "" })}><i className="fa-solid fa-rotate-left" /> Reset</Button>
        <div className="ml-auto"><SortControl fields={SORT_FIELDS} value={sort} onChange={setSort} testid="sellers-sort" /></div>
      </div>

      <Table head={["Company", "Type", "Mobile", "City", "Products", "Subscription", "Visibility", "Badge", "Payment", "Actions"]}>
        {sortRows(rows, SORT_FIELDS, sort).map((s) => (
          <tr key={s.id} data-testid={`seller-row-${s.id}`}>
            <td className="px-4 py-3 font-semibold text-slate-800">{s.company_name || "—"}<div className="text-xs text-slate-400">{s.gst || "No GST"}</div></td>
            <td className="px-4 py-3 capitalize">{s.seller_type}</td>
            <td className="px-4 py-3">{s.phone}</td>
            <td className="px-4 py-3">{s.city || "—"}</td>
            <td className="px-4 py-3" data-testid={`seller-products-${s.id}`}>
              <div className="flex items-center gap-2">
                <div>
                  <span className="font-bold text-slate-800">{s.product_count ?? 0}</span>
                  <span className="text-slate-400"> / {s.total_slots ?? 0}</span>
                  <div className="text-xs text-slate-400">listed / allowed</div>
                </div>
                <button onClick={() => editSlots(s)} data-testid={`seller-slots-edit-${s.id}`} title="Edit number of products" className="text-slate-400 hover:text-orange-600"><i className="fa-solid fa-pen text-xs" /></button>
              </div>
            </td>
            <td className="px-4 py-3"><Badge tone={s.subscription?.status === "active" ? "green" : "slate"}>{s.subscription?.plan || s.subscription?.status || "inactive"}</Badge></td>
            <td className="px-4 py-3">
              <button onClick={() => toggleVis(s.id)} data-testid={`vis-${s.id}`} className={`rounded-full px-3 py-1 text-xs font-bold ${s.visibility ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{s.visibility ? "ON" : "OFF"}</button>
            </td>
            <td className="px-4 py-3"><Badge tone={s.badge?.status === "active" ? "green" : "slate"}>{s.badge?.status || "inactive"}</Badge></td>
            <td className="px-4 py-3">
              {(() => {
                const pay = latestPayment(s.id);
                if (!pay) return <span className="text-xs text-slate-300">—</span>;
                const pending = pay.status !== "approved" && pay.status !== "rejected";
                return (
                  <div className="flex items-center gap-2">
                    {pay.screenshot
                      ? <img src={mediaUrl(pay.screenshot)} alt="payment" data-testid={`seller-pay-shot-${s.id}`} onClick={() => setShot(mediaUrl(pay.screenshot))} className="h-10 w-10 flex-shrink-0 cursor-pointer rounded border border-slate-200 object-cover hover:opacity-80" />
                      : <span className="text-xs text-slate-400">No shot</span>}
                    <div className="flex flex-col gap-1">
                      <Badge tone={pay.status === "approved" ? "green" : pay.status === "rejected" ? "red" : "amber"}>{pay.status}</Badge>
                      {pending && (
                        <div className="flex gap-1">
                          <button onClick={() => payAction(pay.id, "approved")} data-testid={`seller-pay-approve-${s.id}`} className="rounded bg-emerald-600 px-2 py-0.5 text-xs font-bold text-white hover:bg-emerald-700">Approve</button>
                          <button onClick={() => payAction(pay.id, "rejected")} data-testid={`seller-pay-reject-${s.id}`} className="rounded bg-slate-200 px-2 py-0.5 text-xs font-bold text-slate-600 hover:bg-slate-300">Reject</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </td>
            <td className="px-4 py-3">
              <div className="flex flex-wrap gap-1">
                <Button variant="outline" onClick={() => openEdit(s)} data-testid={`seller-edit-${s.id}`}><i className="fa-solid fa-pen" /></Button>
                <Button variant="ghost" onClick={() => resetPw(s)} data-testid={`seller-resetpw-${s.id}`} title="Reset password to mobile number"><i className="fa-solid fa-key text-amber-600" /></Button>
                {s.badge?.status === "active"
                  ? <Button variant="ghost" onClick={() => setBadge(s.id, "inactive")} data-testid={`badge-off-${s.id}`}>Revoke</Button>
                  : <Button variant="ghost" onClick={() => setBadge(s.id, "active")} data-testid={`badge-on-${s.id}`}>Badge</Button>}
                <Button variant="ghost" onClick={() => del(s)} data-testid={`seller-del-${s.id}`}><i className="fa-solid fa-trash text-red-500" /></Button>
              </div>
            </td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">No sellers</td></tr>}
      </Table>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setModal(null)}>
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 ds-scroll" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-head text-xl font-extrabold text-slate-900">{modal.mode === "create" ? "Create Seller" : "Edit Seller"}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field label="Mobile Number" required><Input value={modal.data.phone} onChange={(e) => setF("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} disabled={modal.mode === "edit"} data-testid="sf-phone" /></Field>
              <Field label="Seller Type"><Select value={modal.data.seller_type} onChange={(e) => setF("seller_type", e.target.value)}><option value="retailer">Retailer</option><option value="wholesaler">Wholesaler</option><option value="manufacturer">Manufacturer</option></Select></Field>
              <Field label="Company Name"><Input value={modal.data.company_name} onChange={(e) => setF("company_name", e.target.value)} data-testid="sf-company" /></Field>
              <Field label="Trade Name"><Input value={modal.data.trade_name} onChange={(e) => setF("trade_name", e.target.value)} /></Field>
              <Field label="City"><Input value={modal.data.city} onChange={(e) => setF("city", e.target.value)} /></Field>
              <Field label="Locality"><Input value={modal.data.locality} onChange={(e) => setF("locality", e.target.value)} /></Field>
              <Field label="Contact Person"><Input value={modal.data.contact_person} onChange={(e) => setF("contact_person", e.target.value)} /></Field>
              <Field label="WhatsApp"><Input value={modal.data.whatsapp} onChange={(e) => setF("whatsapp", e.target.value.replace(/\D/g, "").slice(0, 10))} /></Field>
              <Field label="Email"><Input value={modal.data.email} onChange={(e) => setF("email", e.target.value)} /></Field>
              {modal.mode === "edit" && <>
                <Field label="GST"><Input value={modal.data.gst} onChange={(e) => setF("gst", e.target.value.toUpperCase())} /></Field>
                <Field label="Subscription Plan"><Input value={modal.data.subscription_plan} onChange={(e) => setF("subscription_plan", e.target.value)} placeholder="e.g. Retailer Annual" /></Field>
                <Field label="Subscription Status"><Select value={modal.data.subscription_status} onChange={(e) => setF("subscription_status", e.target.value)}><option value="">—</option><option value="active">active</option><option value="inactive">inactive</option><option value="expired">expired</option></Select></Field>
              </>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
              <Button onClick={save} data-testid="sf-save">{modal.mode === "create" ? "Create" : "Save"}</Button>
            </div>
          </div>
        </div>
      )}

      {shot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShot(null)} data-testid="seller-pay-lightbox">
          <button className="absolute right-5 top-5 text-2xl text-white/80 hover:text-white" onClick={() => setShot(null)}><i className="fa-solid fa-xmark" /></button>
          <img src={shot} alt="Payment screenshot" className="max-h-[90vh] max-w-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
